<template>
  <SubPageShell title="ประวัติการรักษา" nav="history">
    <template #actions>
      <button
        class="ss-icon-btn"
        type="button"
        aria-label="ค้นหา"
        @click="onSearchDeferred"
      >
        <el-icon :size="20"><Search /></el-icon>
      </button>
      <button
        class="ss-icon-btn"
        type="button"
        aria-label="ดาวน์โหลด"
        @click="onExportDeferred"
      >
        <el-icon :size="20"><Download /></el-icon>
      </button>
    </template>

    <!-- Patient summary hero -->
    <section class="h-hero">
      <div class="h-hero-patient">
        <div class="h-hero-avatar">
          <img
            v-if="liffProfile?.pictureUrl"
            :src="liffProfile.pictureUrl"
            :alt="liffProfile.displayName"
          />
          <el-icon v-else :size="22"><User /></el-icon>
        </div>
        <div class="h-hero-info">
          <div class="h-hero-name">{{ displayName }}</div>
          <div class="h-hero-meta">{{ heroMetaLine }}</div>
        </div>
      </div>
      <div class="h-hero-stats">
        <div class="h-hero-stat">
          <div class="h-hero-num">{{ visitsTotal }}</div>
          <div class="h-hero-stat-label">ครั้งที่เข้ารับบริการ</div>
        </div>
        <div class="h-hero-stat-divider" />
        <div class="h-hero-stat">
          <div class="h-hero-num">{{ diseaseCount }}</div>
          <div class="h-hero-stat-label">โรคประจำตัว</div>
        </div>
        <div class="h-hero-stat-divider" />
        <div class="h-hero-stat">
          <div class="h-hero-num">{{ allergyCount }}</div>
          <div class="h-hero-stat-label">การแพ้ยา</div>
        </div>
      </div>
    </section>

    <!-- Quick cards — last-visit & latest visit lab -->
    <div class="h-quick">
      <div class="h-quick-card" @click="activeFilter = 'visit'">
        <div class="h-quick-head">
          <div class="h-quick-icon last">
            <el-icon :size="17"><Clock /></el-icon>
          </div>
          <div class="h-quick-label">พบแพทย์ล่าสุด</div>
        </div>
        <div class="h-quick-value">{{ latestVisitText }}</div>
        <div class="h-quick-sub link">ดูประวัตินัด ›</div>
      </div>
      <div class="h-quick-card lab-card" @click="activeFilter = 'lab'">
        <div class="h-quick-head">
          <div class="h-quick-icon lab">
            <el-icon :size="17"><DataLine /></el-icon>
          </div>
          <div class="h-quick-label">ผลแลปตรวจรักษา</div>
        </div>
        <div class="h-quick-value">{{ latestLabDateText }}</div>
        <div class="h-quick-sub link">ดูผลแลป {{ labOrders.length ? `(${labOrders.length} รอบ) ›` : '›' }}</div>
      </div>
    </div>

    <!-- Allergy banner -->
    <div class="h-allergy">
      <el-icon :size="22" color="#C97A0C"><WarningFilled /></el-icon>
      <div>
        <div class="h-allergy-title">{{ allergyHeadline }}</div>
        <div class="h-allergy-desc">{{ allergyHelp }}</div>
      </div>
    </div>

    <!-- Main Tab Selection on History Page: Visit Labs vs Doctor Visits vs All Timeline -->
    <div class="h-main-tab-bar">
      <button
        type="button"
        class="h-main-tab-pill"
        :class="{ active: activeFilter === 'lab' }"
        @click="activeFilter = 'lab'"
      >
        <el-icon :size="16"><DataLine /></el-icon>
        <span>ผลแลปตรวจรักษา</span>
        <span v-if="labOrders.length" class="h-tab-pill-badge">{{ labOrders.length }}</span>
      </button>

      <button
        type="button"
        class="h-main-tab-pill"
        :class="{ active: activeFilter === 'visit' }"
        @click="activeFilter = 'visit'"
      >
        <el-icon :size="16"><FirstAidKit /></el-icon>
        <span>การพบแพทย์</span>
        <span v-if="pastTotal" class="h-tab-pill-badge">{{ pastTotal }}</span>
      </button>

      <button
        type="button"
        class="h-main-tab-pill"
        :class="{ active: activeFilter === 'all' }"
        @click="activeFilter = 'all'"
      >
        <span>ไทม์ไลน์รวม</span>
      </button>
    </div>

    <!-- Lab-specific info & subfilters when activeFilter === 'lab' -->
    <div v-if="activeFilter === 'lab'" class="h-lab-intro-banner">
      <div class="h-lab-intro-title">
        <el-icon :size="18" color="#0284c7"><DataLine /></el-icon>
        <span>ผลการตรวจแลปตรวจรักษา (Visit Labs)</span>
      </div>
      <div class="h-lab-intro-desc">
        ประวัติผลแลปที่สั่งตรวจในการรักษา (ตาราง tvisit_lab_order) บันทึกแยกตามรอบตรวจเพื่อติดตามอาการอย่างต่อเนื่อง (แยกคนละส่วนกับผลตรวจสุขภาพประจำปีในแท็บ "ผลตรวจ")
      </div>
      <div class="h-lab-subfilters">
        <button
          type="button"
          class="h-lab-subchip"
          :class="{ active: labSubFilter === 'all' }"
          @click="labSubFilter = 'all'"
        >
          รอบตรวจทั้งหมด ({{ labOrders.length }})
        </button>
        <button
          type="button"
          class="h-lab-subchip"
          :class="{ active: labSubFilter === 'abnormal' }"
          @click="labSubFilter = 'abnormal'"
        >
          ⚠️ มีค่าต้องติดตาม
        </button>
        <button
          type="button"
          class="h-lab-subchip"
          :class="{ active: labSubFilter === 'pending' }"
          @click="labSubFilter = 'pending'"
        >
          ⏳ รอผลตรวจ
        </button>
      </div>
    </div>

    <!-- Timeline — Phase 12.3 wired to live past appointments -->
    <div class="h-timeline">
      <div v-if="loading && timelineTotalCount === 0" class="h-empty">
        <el-icon :size="32" class="h-empty-icon"><Loading /></el-icon>
        <div>กำลังโหลดประวัติ...</div>
      </div>
      <div v-else-if="!loading && filteredGroups.length === 0" class="h-empty">
        <template v-if="activeFilter === 'lab'">
          <el-icon :size="38" class="h-empty-icon" style="color: #0284c7;"><DataLine /></el-icon>
          <div style="font-size: 15px; color: #1e293b;">ยังไม่มีประวัติผลแลปตรวจรักษา</div>
          <div class="h-empty-sub">
            เมื่อแพทย์สั่งตรวจเลือดหรือตรวจทางห้องปฏิบัติการในการรักษา ผลจะแสดงที่นี่ตามรอบตรวจ (แยกคนละส่วนกับผลตรวจสุขภาพประจำปี)
          </div>
        </template>
        <template v-else>
          <el-icon :size="32" class="h-empty-icon"><Document /></el-icon>
          <div>ยังไม่มีประวัติในหมวดนี้</div>
          <div class="h-empty-sub">
            เมื่อมีนัดหรือการตรวจผลแลป จะแสดงที่นี่
          </div>
        </template>
      </div>

      <template v-for="grp in filteredGroups" :key="grp.label">
        <div class="h-timeline-month">{{ grp.label }}</div>
        <div v-for="item in grp.items" :key="item.id" class="h-tl-item">
          <!-- 1) Appointment Card -->
          <template v-if="item.kind === 'appointment'">
            <div class="h-tl-dot" :class="typeDotClass(item.appt.appointmentType)">
              <el-icon :size="20">
                <component :is="typeIcon(item.appt.appointmentType)" />
              </el-icon>
            </div>
            <div class="h-tl-card" @click="onAppointmentDeferred">
              <div class="h-tl-head">
                <span
                  class="h-tl-pill"
                  :class="typePillClass(item.appt.appointmentType)"
                >
                  {{ typeLabel(item.appt.appointmentType) }}
                </span>
                <span class="h-tl-date">{{
                  formatThaiShort(item.appt.scheduledAt)
                }}</span>
              </div>
              <div class="h-tl-title">
                {{
                  item.appt.diagnosis ??
                  item.appt.complaint ??
                  typeLabel(item.appt.appointmentType)
                }}
              </div>
              <div v-if="item.appt.doctorName" class="h-tl-sub">
                {{ item.appt.doctorName }}
              </div>
              <div class="h-tl-meta">
                <el-icon v-if="item.appt.appointmentType === 'TELEMED'" :size="12">
                  <VideoCamera />
                </el-icon>
                <el-icon v-else :size="12"><OfficeBuilding /></el-icon>
                <span>{{ statusLabel(item.appt.status) }}</span>
              </div>
            </div>
          </template>

          <!-- 2) Visit Lab Card (แสดงเป็นรอบการตรวจ lab order) -->
          <template v-else-if="item.kind === 'lab'">
            <div class="h-tl-dot lab">
              <el-icon :size="20"><DataLine /></el-icon>
            </div>
            <div class="h-tl-card h-tl-lab-card" @click="goVisitLab(item.lab.orderId)">
              <div class="h-lab-round-head">
                <div class="h-lab-round-title">
                  <span class="h-lab-round-tag">รอบตรวจแลป</span>
                  <span class="h-lab-round-date">
                    <el-icon :size="12" style="vertical-align: middle; margin-right: 2px;"><Calendar /></el-icon>
                    {{ formatThaiDateWithTime(item.lab.orderedAt) }}
                  </span>
                </div>
                <span class="h-lab-status-badge" :class="labStatusPill(item.lab.orderStatus).class">
                  {{ labStatusPill(item.lab.orderStatus).label }}
                </span>
              </div>

              <div class="h-lab-location">
                <el-icon :size="13"><OfficeBuilding /></el-icon>
                <span>{{ item.lab.labName || "ห้องปฏิบัติการทางการแพทย์" }}</span>
              </div>

              <div v-if="item.lab.doctorName" class="h-lab-doctor">
                <el-icon :size="13"><User /></el-icon>
                <span>แพทย์ผู้สั่งตรวจ: {{ item.lab.doctorName }}</span>
              </div>

              <div v-if="item.lab.clinicalNote" class="h-lab-clinical-note">
                <el-icon :size="13"><Document /></el-icon>
                <span>ข้อบ่งชี้: {{ item.lab.clinicalNote }}</span>
              </div>

              <div
                v-if="item.lab.resultSummary && (item.lab.orderStatus === 'RESULTED' || item.lab.orderStatus === 'PARTIAL')"
                class="h-lab-summary-row"
              >
                <span v-if="item.lab.resultSummary.bad > 0" class="h-lab-chip bad">
                  🔴 ผิดปกติ {{ item.lab.resultSummary.bad }}
                </span>
                <span v-if="item.lab.resultSummary.warn > 0" class="h-lab-chip warn">
                  🟠 เฝ้าระวัง {{ item.lab.resultSummary.warn }}
                </span>
                <span v-if="item.lab.resultSummary.ok > 0" class="h-lab-chip ok">
                  🟢 ปกติ {{ item.lab.resultSummary.ok }}
                </span>
                <span class="h-lab-chip total">
                  รวม {{ item.lab.resultSummary.total }} รายการ
                </span>
              </div>
              <div v-else-if="item.lab.orderStatus === 'PENDING'" class="h-lab-pending-notice">
                <el-icon :size="14" class="is-loading"><Loading /></el-icon>
                <span>ตัวอย่างส่งตรวจแล้ว อยู่ระหว่างรอผลจากห้องปฏิบัติการ</span>
              </div>

              <div v-if="item.lab.requiresInPersonPickup" class="h-lab-sensitive-badge">
                <el-icon :size="14"><WarningFilled /></el-icon>
                <span>มีผลตรวจเฉพาะบุคคล (ต้องติดต่อรับผลที่โรงพยาบาลด้วยตนเอง)</span>
              </div>

              <div class="h-lab-card-footer">
                <span class="h-lab-view-link">ดูรายละเอียดผลตรวจและคำแนะนำ</span>
                <el-icon :size="14"><ArrowRight /></el-icon>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>

    <button
      v-if="canLoadMore"
      class="h-load-more"
      type="button"
      :disabled="loading"
      @click="onLoadMore"
    >
      <el-icon :size="16"><Refresh /></el-icon>
      <span>{{ loading ? "กำลังโหลด..." : "โหลดประวัติก่อนหน้า" }}</span>
    </button>

    <div class="h-pdpa">
      <el-icon :size="18" color="#1F7A4D"><CircleCheck /></el-icon>
      <div class="h-pdpa-text">
        ข้อมูลของท่านปลอดภัย ตาม พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล (PDPA)
        การเข้าถึงรายละเอียดต้องยืนยันตัวตนทุกครั้ง
      </div>
    </div>
  </SubPageShell>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Search,
  Download,
  User,
  WarningFilled,
  Clock,
  FirstAidKit,
  VideoCamera,
  DataLine,
  Apple,
  Document,
  OfficeBuilding,
  Refresh,
  CircleCheck,
  Loading,
  ArrowRight,
  Calendar,
} from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import SubPageShell from "@/components/customer/SubPageShell.vue";
import { useLiff } from "@/composables/useLiff";
import { useCustomerMe } from "@/composables/useCustomerMe";
import { useCustomerAppointments } from "@/composables/useCustomerAppointments";
import { useVisitLabs } from "@/composables/useVisitLabs";
import type { AppointmentType, AppointmentStatus } from "@/api/appointment.api";
import type { VisitLabOrderSummary } from "@/api/visitLab.api";

const route = useRoute();
const router = useRouter();
const { profile: liffProfile, init: initLiff } = useLiff();
const { me, load: loadMe } = useCustomerMe();
const {
  upcoming,
  past,
  pastTotal,
  pastHasMore,
  loading: apptLoading,
  refreshUpcoming,
  refreshPast,
  loadMorePast,
} = useCustomerAppointments();

// ─── Visit Lab data ────────────────────────────────────────────────────────────
// แยกออกจาก Appointment timeline อย่างสมบูรณ์
const {
  items: labOrders,
  hasMore: labHasMore,
  loading: labLoading,
  refresh: refreshLabs,
  loadMore: loadMoreLabs,
} = useVisitLabs();

// Quick cards info
const latestLab = computed(() => (labOrders.value.length ? labOrders.value[0] : null));
const latestLabDateText = computed(() => {
  if (latestLab.value) {
    const d = new Date(latestLab.value.orderedAt);
    if (!Number.isNaN(d.getTime())) {
      return `${d.getDate()} ${THAI_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`;
    }
  }
  return "ยังไม่มีรอบตรวจ";
});

const latestVisit = computed(() => {
  if (past.value.length) return past.value[0];
  if (upcoming.value.length) return upcoming.value[0];
  return null;
});
const latestVisitText = computed(() => {
  if (latestVisit.value) {
    const d = new Date(latestVisit.value.scheduledAt);
    if (!Number.isNaN(d.getTime())) {
      return `${d.getDate()} ${THAI_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`;
    }
  }
  return "ยังไม่มีประวัติตรวจ";
});

const displayName = computed(() => {
  if (me.value?.fname || me.value?.lname) {
    return `คุณ${me.value.fname ?? ""} ${me.value.lname ?? ""}`.trim();
  }
  return liffProfile.value?.displayName ?? "ผู้ใช้";
});

// Phase 12.3 — live hero stats. Total visits = pastTotal (BE-reported). The
// disease / allergy counts come from the comma-/newline-separated free-text
// fields the customer maintains on the Profile page; null falls back to 0.
const heroMetaLine = computed(() => {
  const bits: string[] = [];
  if (me.value?.idcardMasked) bits.push(me.value.idcardMasked);
  if (me.value?.age != null) bits.push(`${me.value.age} ปี`);
  return bits.length ? bits.join(" · ") : "—";
});
function splitCount(text: string | null | undefined): number {
  if (!text) return 0;
  return text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0).length;
}
const visitsTotal = computed(() => pastTotal.value ?? 0);
const diseaseCount = computed(() => splitCount(me.value?.diseaseinfo ?? null));
const allergyCount = computed(() => splitCount(me.value?.allergies ?? null));

const allergyHeadline = computed(() => {
  if (me.value?.allergies) return `แพ้ยา/แพ้อาหาร: ${me.value.allergies}`;
  return "ยังไม่ได้ระบุการแพ้ยา";
});
const allergyHelp = computed(() => {
  if (me.value?.allergies) return "บันทึกในเวชระเบียน · แจ้งแพทย์ทุกครั้ง";
  return "กรุณาเพิ่มในหน้าโปรไฟล์ เพื่อให้แพทย์เห็นทุกครั้งที่ปรึกษา";
});

const initialTab =
  (route.query.tab as string) === "lab"
    ? "lab"
    : (route.query.tab as string) === "visit"
      ? "visit"
      : "all";

const activeFilter = ref<"all" | "visit" | "lab" | "vaccine" | "drug" | "tele">(
  initialTab,
);
const labSubFilter = ref<"all" | "abnormal" | "pending">("all");

// Unified loading & count for appointment + visit lab timeline.
const loading = computed(() => apptLoading.value || labLoading.value);
const timelineTotalCount = computed(() => upcoming.value.length + past.value.length + labOrders.value.length);

type TimelineItem =
  | { kind: "appointment"; id: string; date: Date; appt: AppointmentRow }
  | { kind: "lab"; id: string; date: Date; lab: VisitLabOrderSummary };

const THAI_MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];
const THAI_MONTHS_SHORT = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

function typeLabel(t: AppointmentType): string {
  if (t === "TELEMED") return "Telemed";
  if (t === "FOLLOWUP") return "ติดตามอาการ";
  return "ตรวจรักษา";
}
function typeIcon(t: AppointmentType) {
  if (t === "TELEMED") return VideoCamera;
  if (t === "FOLLOWUP") return Apple;
  return FirstAidKit;
}
function typePillClass(t: AppointmentType): string {
  if (t === "TELEMED") return "red";
  if (t === "FOLLOWUP") return "amber";
  return "blue";
}
function typeDotClass(t: AppointmentType): string {
  if (t === "TELEMED") return "specialist";
  if (t === "FOLLOWUP") return "vaccine";
  return "checkup";
}
function statusLabel(s: AppointmentStatus): string {
  if (s === "COMPLETED") return "สิ้นสุดการรักษา";
  if (s === "CANCELLED") return "ยกเลิกแล้ว";
  if (s === "NO_SHOW") return "ไม่มาตามนัด";
  if (s === "WAITING_MED_RECORD") return "รอยืนยัน";
  return "เลยกำหนดเวลา";
}
function formatThaiShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return `${d.getUTCDate()} ${THAI_MONTHS_SHORT[d.getUTCMonth()]}`;
}

function formatThaiDateWithTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dateStr = `${d.getDate()} ${THAI_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`;
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${dateStr} · ${hours}:${mins} น.`;
}

function labStatusPill(status: string) {
  if (status === "RESULTED") return { label: "ผลออกแล้ว", class: "status-resulted" };
  if (status === "PARTIAL") return { label: "ผลออกบางส่วน", class: "status-partial" };
  if (status === "PENDING") return { label: "รอผลตรวจ", class: "status-pending" };
  if (status === "CANCELLED") return { label: "ยกเลิกแล้ว", class: "status-cancelled" };
  return { label: status, class: "status-pending" };
}

// Filter past appointments by chip — "all" passes everything.
function chipMatches(
  filter: (typeof activeFilter)["value"],
  t: AppointmentType,
): boolean {
  if (filter === "all") return true;
  if (filter === "tele") return t === "TELEMED";
  if (filter === "visit") return t === "IN_PERSON" || t === "FOLLOWUP";
  return false;
}

type AppointmentRow = (typeof past.value)[number];

const filteredGroups = computed(() => {
  const allItems: TimelineItem[] = [];

  // Appointments (รวมทั้งนัดหมายที่จะถึงและที่ผ่านมาแล้วเมื่อ filter ตรง)
  if (activeFilter.value !== "lab") {
    const seenApptIds = new Set<string>();
    const allAppts = [...upcoming.value, ...past.value];
    for (const a of allAppts) {
      if (seenApptIds.has(a.id)) continue;
      seenApptIds.add(a.id);
      if (chipMatches(activeFilter.value, a.appointmentType)) {
        allItems.push({
          kind: "appointment",
          id: a.id,
          date: new Date(a.scheduledAt),
          appt: a,
        });
      }
    }
  }

  // Visit Lab Orders (เฉพาะผลที่มาตรวจ ไม่เกี่ยวกับตรวจสุขภาพ)
  if (activeFilter.value === "all" || activeFilter.value === "lab") {
    let orders = labOrders.value;
    if (activeFilter.value === "lab") {
      if (labSubFilter.value === "abnormal") {
        orders = orders.filter(
          (l) =>
            (l.resultSummary?.bad ?? 0) > 0 ||
            (l.resultSummary?.warn ?? 0) > 0 ||
            l.overallStatus === "bad" ||
            l.overallStatus === "warn",
        );
      } else if (labSubFilter.value === "pending") {
        orders = orders.filter(
          (l) =>
            l.orderStatus === "PENDING" ||
            l.orderStatus === "PARTIAL" ||
            l.overallStatus === "pending",
        );
      }
    }
    for (const l of orders) {
      allItems.push({
        kind: "lab",
        id: l.orderId,
        date: new Date(l.orderedAt),
        lab: l,
      });
    }
  }

  // Sort newest first
  allItems.sort((x, y) => y.date.getTime() - x.date.getTime());

  // Group by month
  const groups: Array<{ label: string; items: TimelineItem[] }> = [];
  let currentKey: string | null = null;

  for (const item of allItems) {
    const key = `${item.date.getUTCFullYear()}-${item.date.getUTCMonth()}`;
    if (key !== currentKey) {
      const label = `${THAI_MONTHS[item.date.getUTCMonth()]} ${item.date.getUTCFullYear() + 543}`;
      groups.push({ label, items: [] });
      currentKey = key;
    }
    groups[groups.length - 1].items.push(item);
  }

  return groups;
});

// Navigation to Visit Lab Detail
function goVisitLab(orderId: string): void {
  router.push({
    name: "customer-visit-lab-detail",
    params: { orderId },
  });
}

const canLoadMore = computed(() => {
  if (activeFilter.value === "lab") return labHasMore.value;
  return pastHasMore.value || labHasMore.value;
});

async function onLoadMore(): Promise<void> {
  if (!canLoadMore.value) {
    ElMessage.info("ไม่มีประวัติเก่ากว่านี้แล้ว");
    return;
  }
  if (activeFilter.value === "lab") {
    await loadMoreLabs();
  } else if (activeFilter.value === "all") {
    await Promise.all([
      pastHasMore.value ? loadMorePast(20) : Promise.resolve(),
      labHasMore.value ? loadMoreLabs() : Promise.resolve(),
    ]);
  } else {
    await loadMorePast(20);
  }
}

// Phase 12.3 — DEFER groups, each targeting a different upcoming feature.
function onSearchDeferred(): void {
  ElMessage.info("ค้นหาประวัติการรักษา จะเปิดในเฟสถัดไป");
}
function onExportDeferred(): void {
  ElMessage.info("ดาวน์โหลดประวัติย้อนหลังเป็น PDF จะเปิดในเฟสถัดไป");
}
function onAppointmentDeferred(): void {
  router.push({ name: "customer-appointment" });
}

function bounceToRegister(): void {
  const lastKey = sessionStorage.getItem("register:lastContractKey");
  if (lastKey)
    router.replace({
      name: "customer-register",
      params: { contractKey: lastKey },
    });
  else router.replace({ name: "not-found" });
}

onMounted(async () => {
  await initLiff();
  const result = await loadMe(true);
  if (!result) {
    bounceToRegister();
    return;
  }
  // Fire-and-forget — empty state renders while it loads.
  refreshUpcoming();
  refreshPast();
  refreshLabs();
});
</script>

<style scoped>
.h-hero {
  margin: 16px;
  background: var(--c-header-grad);
  border-radius: 22px;
  padding: 18px 20px;
  color: #ffffff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.24);
}
.h-hero::before,
.h-hero::after {
  content: "";
  position: absolute;
  border-radius: 50%;
}
.h-hero::before {
  top: -50px;
  right: -50px;
  width: 160px;
  height: 160px;
  background: rgba(255, 255, 255, 0.12);
}
.h-hero::after {
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.08);
}
.h-hero-patient {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
}
.h-hero-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  overflow: hidden;
}
.h-hero-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.h-hero-info {
  flex: 1;
  min-width: 0;
}
.h-hero-name {
  font-size: 14px;
  font-weight: 700;
}
.h-hero-meta {
  font-size: 11px;
  opacity: 0.88;
  margin-top: 2px;
}
.h-hero-stats {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 0.5px solid rgba(255, 255, 255, 0.25);
  position: relative;
  z-index: 1;
}
.h-hero-stat {
  flex: 1;
}
.h-hero-num {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
}
.h-hero-stat-label {
  font-size: 11px;
  opacity: 0.88;
  margin-top: 4px;
}
.h-hero-stat-divider {
  width: 0.5px;
  background: rgba(255, 255, 255, 0.25);
}

.h-empty {
  text-align: center;
  padding: 32px 16px;
  color: var(--c-ink-soft);
}
.h-empty-icon {
  color: var(--c-primary-soft);
  margin-bottom: 8px;
}
.h-empty > div {
  font-size: 14px;
  font-weight: 600;
}
.h-empty-sub {
  font-size: 12px !important;
  font-weight: 400 !important;
  margin-top: 4px;
}

.h-quick {
  margin: 12px 16px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.h-quick-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 14px;
  cursor: pointer;
  box-shadow: var(--c-card-shadow-soft);
}
.h-quick-card.readonly {
  cursor: default;
}
.h-quick-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.h-quick-icon {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.h-quick-icon.last {
  background: #ffe8ec;
  color: #d83a5a;
}
.h-quick-icon.med {
  background: #e6f2fd;
  color: #2563c7;
}
.h-quick-icon.lab {
  background: #e0f2fe;
  color: #0284c7;
}
.h-quick-card.lab-card {
  border: 1px solid #e0f2fe;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
}
.h-quick-label {
  font-size: 11px;
  color: var(--c-ink-soft);
}
.h-quick-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--c-ink);
  margin-top: 8px;
}
.h-quick-sub {
  font-size: 11px;
  margin-top: 2px;
}
.h-quick-sub.muted {
  color: var(--c-ink-soft);
}
.h-quick-sub.link {
  color: var(--c-primary);
  cursor: pointer;
}

/* ─── Main Tabs Switcher on History Page ─────────────────────────────────── */
.h-main-tab-bar {
  margin: 12px 16px 10px;
  background: #f1f5f9;
  border-radius: 14px;
  padding: 4px;
  display: flex;
  gap: 4px;
}
.h-main-tab-pill {
  flex: 1;
  background: transparent;
  border: none;
  padding: 9px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.h-main-tab-pill.active {
  background: #ffffff;
  color: #0284c7;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.h-tab-pill-badge {
  font-size: 10px;
  background: #0284c7;
  color: #fff;
  padding: 1px 6px;
  border-radius: 999px;
  font-weight: 700;
  line-height: 1.2;
}
.h-main-tab-pill.active .h-tab-pill-badge {
  background: #0284c7;
}

.h-allergy {
  margin: 10px 16px 0;
  background: #fff8e6;
  border-radius: 14px;
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: center;
}
.h-allergy-title {
  font-size: 12px;
  font-weight: 700;
  color: #8a5a0c;
}
.h-allergy-desc {
  font-size: 11px;
  color: #6b5320;
  margin-top: 2px;
}

.h-section {
  margin: 22px 16px 10px;
  font-size: 15px;
  font-weight: 700;
  color: var(--c-ink);
}

.h-filters {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  margin: 0 16px 14px;
  padding-bottom: 4px;
  scrollbar-width: none;
}
.h-filters::-webkit-scrollbar {
  display: none;
}
.h-chip {
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  border: none;
  font-family: inherit;
  background: #ffffff;
  color: var(--c-ink-soft);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  box-shadow: var(--c-card-shadow-soft);
  transition: all 0.15s;
}
.h-chip.active {
  background: var(--c-primary);
  color: #ffffff;
  font-weight: 700;
}

.h-timeline {
  margin: 0 16px;
  position: relative;
}
.h-timeline::before {
  content: "";
  position: absolute;
  left: 19px;
  top: 12px;
  bottom: 12px;
  width: 2px;
  background: linear-gradient(180deg, #c5d2e8 0%, #e8f0fe 100%);
}
.h-timeline-month {
  font-size: 11px;
  color: var(--c-ink-soft);
  margin: 0 0 10px 50px;
  font-weight: 700;
}
.h-timeline-month:not(:first-child) {
  margin-top: 14px;
}
.h-tl-item {
  position: relative;
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}
.h-tl-dot {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
  border: 3px solid #f5f8fe;
  color: #ffffff;
}
.h-tl-dot.checkup {
  background: var(--c-primary);
}
.h-tl-dot.lab {
  background: #2563c7;
}
.h-tl-dot.pharma {
  background: #5bc78a;
}
.h-tl-dot.specialist {
  background: #d83a5a;
}
.h-tl-dot.vaccine {
  background: #c97a0c;
}
.h-tl-dot.muted {
  background: #c5d2e8;
}
.h-tl-card {
  flex: 1;
  background: #ffffff;
  border-radius: 14px;
  padding: 12px 14px;
  min-width: 0;
  box-shadow: var(--c-card-shadow-soft);
  cursor: pointer;
  transition: transform 0.12s;
}
.h-tl-card:active {
  transform: scale(0.99);
}
.h-tl-card.muted {
  opacity: 0.85;
}
.h-tl-card.readonly {
  cursor: default;
}
.h-tl-card.readonly:active {
  transform: none;
}
.h-tl-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.h-tl-pill {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 700;
}
.h-tl-pill.blue {
  background: #e8f0fe;
  color: var(--c-primary);
}
.h-tl-pill.cyan {
  background: #e6f2fd;
  color: #2563c7;
}
.h-tl-pill.green {
  background: #eaf7f0;
  color: #1f7a4d;
}
.h-tl-pill.red {
  background: #ffe8ec;
  color: #d83a5a;
}
.h-tl-pill.amber {
  background: #fff4de;
  color: #c97a0c;
}
.h-tl-pill.gray {
  background: #f0f2f7;
  color: var(--c-ink-soft);
}
.h-tl-date {
  font-size: 11px;
  color: var(--c-ink-soft);
}
.h-tl-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--c-ink);
  margin-top: 8px;
}
.h-tl-sub {
  font-size: 12px;
  color: var(--c-ink-soft);
  margin-top: 2px;
}
.h-tl-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 11px;
  color: var(--c-ink-soft);
}
.h-tl-status-row {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}
.h-tl-status {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 999px;
  font-weight: 700;
}
.h-tl-status.ok {
  background: #eaf7f0;
  color: #1f7a4d;
}
.h-tl-status.watch {
  background: #fff4de;
  color: #c97a0c;
}
.h-tl-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 0.5px solid #f0f2f7;
}
.h-tl-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
}
.h-tl-action.primary {
  color: var(--c-primary);
}
.h-tl-action.success {
  color: #1f7a4d;
}
.h-tl-action.locked {
  color: var(--c-ink-soft);
  font-weight: 500;
}

.h-load-more {
  margin: 14px 16px 0;
  width: calc(100% - 32px);
  padding: 14px;
  background: #ffffff;
  border: none;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  color: var(--c-primary);
  font-weight: 700;
  box-shadow: var(--c-card-shadow-soft);
}

.h-pdpa {
  margin: 12px 16px 0;
  background: #f0f2f7;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.h-pdpa-text {
  font-size: 11px;
  color: var(--c-ink-soft);
  line-height: 1.5;
}

/* ─── Lab Section & Round Cards ───────────────────────────────────────────── */
.h-lab-intro-banner {
  margin: 12px 16px 14px;
  background: #ffffff;
  border-radius: 16px;
  padding: 14px 16px;
  border: 1px solid #e0f2fe;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.06);
}
.h-lab-intro-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #0369a1;
}
.h-lab-intro-desc {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.45;
}
.h-lab-subfilters {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.h-lab-subchip {
  background: #f1f5f9;
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}
.h-lab-subchip.active {
  background: #0284c7;
  color: #ffffff;
}

.h-tl-lab-card {
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  border-radius: 16px;
}
.h-lab-round-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.h-lab-round-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.h-lab-round-tag {
  font-size: 11px;
  font-weight: 700;
  color: #0284c7;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.h-lab-round-date {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}
.h-lab-status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.h-lab-status-badge.status-resulted {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}
.h-lab-status-badge.status-partial {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}
.h-lab-status-badge.status-pending {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}
.h-lab-status-badge.status-cancelled {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.h-lab-location,
.h-lab-doctor {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #475569;
  margin-top: 6px;
}
.h-lab-clinical-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #334155;
  background: #f8fafc;
  padding: 6px 10px;
  border-radius: 8px;
  margin-top: 8px;
  border-left: 3px solid #38bdf8;
  font-style: italic;
  line-height: 1.4;
}
.h-lab-summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.h-lab-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}
.h-lab-chip.ok {
  background: #ecfdf5;
  color: #047857;
}
.h-lab-chip.warn {
  background: #fffbeb;
  color: #b45309;
}
.h-lab-chip.bad {
  background: #fef2f2;
  color: #b91c1c;
}
.h-lab-chip.total {
  background: #f1f5f9;
  color: #475569;
}
.h-lab-pending-notice {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  margin-top: 8px;
  background: #f8fafc;
  padding: 6px 10px;
  border-radius: 8px;
}
.h-lab-sensitive-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px dashed #fdba74;
  padding: 6px 10px;
  border-radius: 8px;
  margin-top: 8px;
}
.h-lab-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
  color: #0284c7;
  font-size: 12px;
  font-weight: 700;
}
</style>
